import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, MenuItem,
  FormControl, InputLabel, Select, Alert, CircularProgress, Chip,
  Grid, Paper, IconButton, Tooltip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tab, Tabs
} from '@mui/material';
import {
  Notifications, Warning, Error, Info, CheckCircle, Refresh,
  WaterDrop, Policy, Assignment, Restaurant, Cloud, Memory, Person,
  Schedule, DoneAll, FilterList, AccessTime
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { alertsAPI, pondsAPI } from 'services/api';

// Severity configuration
const SEVERITY_CONFIG = {
  critical: { color: 'error', icon: <Error />, label: 'Critical' },
  warning: { color: 'warning', icon: <Warning />, label: 'Warning' },
  info: { color: 'info', icon: <Info />, label: 'Info' }
};

// Alert type labels
const TYPE_LABELS = {
  doc_reminder: 'DOC Reminder',
  weather_warning: 'Weather Warning',
  disease_alert: 'Disease Alert',
  feed_reminder: 'Feed Reminder',
  policy_expiry: 'Policy Expiry',
  claim_update: 'Claim Update',
  water_quality: 'Water Quality',
  mortality_alert: 'Mortality Alert',
  compliance: 'Compliance',
  general: 'General'
};

const AlertsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState(null);
  const [ponds, setPonds] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    pondId: '',
    severity: '',
    acknowledged: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build params based on tab and filters
      const params = { limit: 100 };
      
      if (tabValue === 0) {
        // Unread tab
        params.acknowledged = false;
      } else if (tabValue === 1) {
        // All tab - no acknowledged filter
      } else if (tabValue === 2) {
        // Acknowledged tab
        params.acknowledged = true;
      }
      
      if (filters.pondId) params.pondId = filters.pondId;
      if (filters.severity) params.severity = filters.severity;
      
      const [alertsRes, countsRes, pondsRes] = await Promise.all([
        alertsAPI.getAll(params),
        alertsAPI.getCount(),
        pondsAPI.getAllForUser()
      ]);
      
      setAlerts(alertsRes.data || []);
      setCounts(countsRes.data);
      setPonds(pondsRes || []);
    } catch (err) {
      console.error('Error loading alerts:', err);
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [tabValue, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAcknowledge = async (alertId) => {
    try {
      await alertsAPI.acknowledge(alertId);
      setSuccess('Alert acknowledged');
      await loadData();
    } catch (err) {
      setError('Failed to acknowledge alert');
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      const result = await alertsAPI.acknowledgeAll();
      setSuccess(`Acknowledged ${result.data.count} alerts`);
      await loadData();
    } catch (err) {
      setError('Failed to acknowledge all alerts');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityChip = (severity) => {
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
            Alerts
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Stay informed about your farm and policies
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {counts?.unacknowledged > 0 && (
            <Button
              variant="outlined"
              startIcon={<DoneAll />}
              onClick={handleAcknowledgeAll}
            >
              Acknowledge All ({counts.unacknowledged})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Stats Cards */}
      {counts && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'grey.100' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{counts.total}</Typography>
                <Typography variant="caption" color="text.secondary">Total</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'primary.50' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {counts.unacknowledged}
                </Typography>
                <Typography variant="caption">Unread</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'error.50' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {counts.critical}
                </Typography>
                <Typography variant="caption">Critical</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'warning.50' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {counts.warning}
                </Typography>
                <Typography variant="caption">Warning</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'info.50' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                  {counts.info}
                </Typography>
                <Typography variant="caption">Info</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: 'success.50' }}>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {counts.action_required}
                </Typography>
                <Typography variant="caption">Action Needed</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs and Filters */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab 
              icon={<Notifications />} 
              label={`Unread (${counts?.unacknowledged || 0})`} 
              iconPosition="start" 
            />
            <Tab icon={<FilterList />} label="All" iconPosition="start" />
            <Tab icon={<CheckCircle />} label="Acknowledged" iconPosition="start" />
          </Tabs>
          
          <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Pond</InputLabel>
              <Select
                value={filters.pondId}
                label="Pond"
                onChange={(e) => setFilters({ ...filters, pondId: e.target.value })}
              >
                <MenuItem value="">All Ponds</MenuItem>
                {ponds.map(pond => (
                  <MenuItem key={pond.id} value={pond.id}>{pond.pond_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Severity</InputLabel>
              <Select
                value={filters.severity}
                label="Severity"
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="info">Info</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Alerts Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Alert</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Severity</strong></TableCell>
                <TableCell><strong>Pond</strong></TableCell>
                <TableCell><strong>Time</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Notifications sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No alerts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                alerts.map(alert => (
                  <TableRow 
                    key={alert.id} 
                    hover
                    sx={{ 
                      bgcolor: alert.acknowledged ? 'transparent' : 
                        alert.severity === 'critical' ? '#ffebee' :
                        alert.severity === 'warning' ? '#fff3e0' : '#e3f2fd'
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {alert.title}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {alert.message}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={TYPE_LABELS[alert.alert_type] || alert.alert_type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{getSeverityChip(alert.severity)}</TableCell>
                    <TableCell>
                      {alert.pond_name ? (
                        <Chip 
                          icon={<WaterDrop sx={{ fontSize: 14 }} />}
                          label={alert.pond_name}
                          size="small"
                          variant="outlined"
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(alert.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      {alert.acknowledged ? (
                        <Chip 
                          icon={<CheckCircle />}
                          label="Read"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip 
                          icon={<AccessTime />}
                          label="Unread"
                          color="primary"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {!alert.acknowledged && (
                        <Tooltip title="Mark as Read">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleAcknowledge(alert.id)}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Info Note */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Alerts are retained for your records and insurance documentation. 
          Acknowledging an alert marks it as read but does not delete it.
        </Typography>
      </Alert>
    </Box>
  );
};

export default AlertsPage;

